import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Icon,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AffiliatedIcon,
  ClaimsDateProvider,
  LoadingView,
  MarketOrTagSelect,
  OrganizationSelect,
  Paginateable,
  PDFExportDialog,
  TaxonomySelect,
  TitleTooltip,
} from 'components';
import {
  PatientType
} from 'doctivity-shared/utils';
import { StoreUtil, SpecialtyUtil } from 'doctivity-shared/utils';
import { MarketsUtil } from 'utils';
import PropTypes from 'prop-types';
import { listMarkets } from 'store/actions/marketsActions';
import { withStyles } from '@mui/styles';
import { PayorSearch, useReportCriteria } from 'market-data';
import { loadAnalytics } from 'store/actions/analyticsActions';
import axiosInstance from 'utils/axiosUtil';
import { TagsUtil } from '../utils';
import { listTagNamespacesByClient } from 'store/actions/tagNamespacesActions';
import fileDownload from 'js-file-download';
import { CsvUtil, DateUtil } from 'doctivity-shared/utils';
import { latestClaimsDate } from '../../config/claims-date';
import { ColorUtil } from 'utils';

const styles = (theme) => ({
  container: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
  },
  filterContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  dropDownContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: theme.spacing(2),
      '& .MuiFormControl-root': {
        width: '100%',
        minWidth: 'unset',
      },
      '& .MuiOutlinedInput-root': {
        height: 44,
      },
      '& .MuiInputLabel-root': {
        fontSize: 14,
      },
      '& .MuiSelect-select': {
        fontSize: 14,
        padding: '10px 14px',
      },
    },
  },
  select: {
    width: '100%',
    height: 57,
    [theme.breakpoints.down('md')]: {
      height: 44,
    },
  },
  filterSecondRow: {
    display: 'flex',
    flex: 1,
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flexBasis: '100%',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gridColumn: '1 / -1',
      gap: theme.spacing(2),
      marginTop: theme.spacing(1),
    },
  },
  filterDropDown: {
    minWidth: 168,
    [theme.breakpoints.down('md')]: {
      minWidth: 'unset',
      width: '100%',
      '& .MuiSelect-select': {
        width: '100% !important',
      },
    },
  },
  filterLabel: {
    [theme.breakpoints.down('md')]: {
      fontSize: 14,
    },
  },
  // Table container
  tableContainer: {
    width: '100%',
    fontSize: 12,
    marginTop: theme.spacing(2),
  },
  // Header row
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#EBF1F8',
    borderRadius: '4px 4px 0 0',
    padding: '8px 0',
    marginBottom: 2,
    width: '100%',
  },
  headerCell: {
    color: '#64748B',
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    padding: '0 8px',
  },
  // Data rows
  dataRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 0',
    borderRadius: 2,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      backgroundColor: '#F8FAFC',
    },
    '&:nth-of-type(even)': {
      backgroundColor: '#F8FAFC',
    },
  },
  cell: {
    padding: '0 8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Column widths - desktop (providers)
  iconCol: {
    width: '2%',
    minWidth: 28,
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      width: 24,
      minWidth: 24,
      padding: 0,
    },
  },
  nameCol: {
    width: '20%',
    minWidth: 120,
  },
  specialtyCol: {
    width: '15%',
    minWidth: 100,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  orgCol: {
    width: '20%',
    minWidth: 100,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  chartCol: {
    width: '42%',
    minWidth: 150,
    paddingRight: 12,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flex: 1,
      width: 'auto',
    },
  },
  // Column widths - desktop (organizations)
  orgNameCol: {
    width: '30%',
    minWidth: 150,
  },
  facilityTypeCol: {
    width: '15%',
    minWidth: 100,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  orgChartCol: {
    width: '52%',
    minWidth: 150,
    paddingRight: 12,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flex: 1,
      width: 'auto',
    },
  },
  // Mobile column widths
  iconColMobile: {
    width: 24,
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
  },
  nameColMobile: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Cell content styles
  providerName: {
    fontWeight: 500,
    color: '#212529',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  orgName: {
    fontWeight: 500,
    color: '#212529',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  specialty: {
    color: '#4B5563',
    fontSize: 11,
    fontWeight: 300,
  },
  facilityType: {
    color: '#4B5563',
    fontSize: 11,
    fontWeight: 300,
  },
  organization: {
    color: '#4B5563',
    fontSize: 11,
    fontWeight: 300,
  },
  affiliatedIcon: {
    fontSize: 14,
    color: '#5391D5',
  },
  // Stacked bar chart
  barContainer: {
    width: '100%',
    height: 20,
    backgroundColor: '#E5E7EB',
    borderRadius: 2,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'row',
  },
  barSegment: {
    height: '100%',
    transition: 'width 0.3s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  barLabel: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: 600,
    textShadow: '0 0 2px rgba(0,0,0,0.3)',
    whiteSpace: 'nowrap',
  },
  // Legend
  legend: {
    display: 'flex',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    flexDirection: 'row',
    gap: theme.spacing(3),
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(2),
    },
  },
  legendRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexDirection: 'row',
  },
  legendItem: {
    height: 14,
    width: 14,
    borderRadius: 7,
    display: 'inline-block',
    marginRight: 6,
  },
  legendItemLabel: {
    color: '#374151',
    fontSize: 12,
  },
  noData: {
    textAlign: 'center',
    paddingTop: 50,
    minHeight: 200,
  },
  pagination: {
    marginTop: theme.spacing(2),
  },
  // Tooltip styles
  tooltipHeader: {
    fontWeight: 600,
    display: 'block',
    marginBottom: 0,
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    padding: theme.spacing(1),
    borderRadius: '4px 4px 0 0',
  },
  tooltipBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    backgroundColor: '#fff',
    padding: theme.spacing(1.5),
    borderRadius: '0 0 4px 4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  tooltipLegendContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 12,
  },
  tooltipPercent: {
    fontWeight: 600,
  },
  infoTip: {
    fontSize: 13,
    fontWeight: 200,
  },
  headerDropDown: {
    minWidth: 220,
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'end',
  },
});
const WhiteTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: 'rgba(0,0,0,0)',
    color: '#000',
    fontSize: 14,
    fontWeight: 400,
    maxWidth: '25rem',
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    minWidth: '15rem',
  },
}))(Tooltip);
const colors = [...ColorUtil.COLORS, '#e3e3e3'];
function PayorChart({ classes} ) {
  const dispatch = useDispatch();
  const [showPdfExport, setShowPdfExport] = useState(false);
  const user = useSelector((state) => state.user);

  const onExportClick = useCallback(async ({ limit, offset = 0, fileName } = {}) => {

    // The table is paged (limit 25), so slicing the loaded rows can't exceed a page.
    // Re-fetch with the dialog's chosen row count / start row for the export.
    let sourceRows = claims?.data?.rows || [];
    if (typeof limit === 'number') {
      try {
        const criteria = getCriteria();
        const filter = {
          ...(criteria.isMarket ? { market: { id: current.market } } : { tag: { id: current.tag } }),
          affiliated_client_id: criteria.affiliated_client_id,
          payors: criteria.payors,
          taxonomies: claimsType === 'providers' ? criteria.taxonomies : null,
          affiliation: criteria.affiliation,
          organization_ids: criteria.organizations,
          singlePayor,
        };
        const response = await axiosInstance.post(
          '/Analytics',
          { type: `${prefix}CLAIMS_PROVIDER_BY_PAYOR`, filter, opts: { limit, offset } },
          { params: { selected_client_id: clientId } },
        );
        sourceRows = response.data?.rows || [];
      } catch (e) {
        console.error('Payor export fetch failed', e);
        sourceRows = (claims?.data?.rows || []).slice(offset, offset + limit);
      }
    }
    const data = sourceRows.map((claim) => {
      const row = [
        claim.provider.npi,
        claim.provider.last_name,
        claim.provider.first_name,
        claim.organization_name || '',
        claim.location_name || '',
        claim.location_address1 || '',
        claim.location_city || '',
        claim.location_state || '',
        claim.location_postal_code || '',
        claim.provider.clients?.length > 0 ? 'Affiliated' : 'Non-Affiliated',
      ];
      // Add percentage columns for each loaded payor
      let totalPct = 0;
      for (let i = 0; i < (loadedPayors?.length || 0) && i < 3; i++) {
        const pct = claim[`percent_${i}`] ?? 0;
        totalPct += pct;
        row.push(pct.toFixed(2));
      }
      // Add Other column
      row.push((100 - totalPct).toFixed(2));
      return row;
    });
    data.unshift([
      'NPI',
      'Last Name',
      'First Name',
      'Organization Name',
      'Location Name',
      'Address',
      'City',
      'State',
      'Postal Code',
      'Affiliation',
      ...(loadedPayors?.map((p) => p.name) || []),
      'Other',
    ]);
    const date = DateUtil.formatDateForUser(new Date());
    fileDownload(
      CsvUtil.matrixToString(data),
      fileName || `doctivity_PROVIDER_PERCENT_PAYOR_${date}.csv`,
    );
  });

  const onExportOrgsClick = useCallback(async ({ limit, offset = 0, fileName } = {}) => {
    // The table is paged (limit 25), so re-fetch with the dialog's chosen count for export.
    let sourceRows = claims?.data?.rows || [];
    if (typeof limit === 'number') {
      try {
        const criteria = getCriteria();
        const filter = {
          ...(criteria.isMarket ? { market: { id: current.market } } : { tag: { id: current.tag } }),
          affiliated_client_id: criteria.affiliated_client_id,
          payors: criteria.payors,
          taxonomies: null,
          affiliation: criteria.affiliation,
          organization_ids: criteria.organizations,
          singlePayor,
        };
        const response = await axiosInstance.post(
          '/Analytics',
          { type: `${prefix}CLAIMS_ORGANIZATION_BY_PAYOR`, filter, opts: { limit, offset } },
          { params: { selected_client_id: clientId } },
        );
        sourceRows = response.data?.rows || [];
      } catch (e) {
        console.error('Payor org export fetch failed', e);
        sourceRows = (claims?.data?.rows || []).slice(offset, offset + limit);
      }
    }
    const data = sourceRows.map((claim) => {
      const row = [
        claim.organization.npi,
        claim.organization.name,
        claim.organization.affiliated?.length > 0 ? 'Affiliated' : 'Non-Affiliated',
      ];
      // Add percentage columns for each loaded payor
      let totalPct = 0;
      for (let i = 0; i < (loadedPayors?.length || 0) && i < 3; i++) {
        const pct = claim[`percent_${i}`] ?? 0;
        totalPct += pct;
        row.push(pct.toFixed(2));
      }
      // Add Other column
      row.push((100 - totalPct).toFixed(2));
      return row;
    });
    data.unshift([
      'NPI',
      'Organization',
      'Affiliation',
      ...(loadedPayors?.map((p) => p.name) || []),
      'Other',
    ]);
    const date = DateUtil.formatDateForUser(new Date());
    fileDownload(
      CsvUtil.matrixToString(data),
      fileName || `doctivity_ORGANIZATION_PERCENT_PAYOR_${date}.csv`,
    );
  });
  const debounceTimeout = useRef(null);
  const [loadedPayors, setLoadedPayors] = useState([]);
  const [prefix, setPrefix] = useState('');
  
  const [singlePayor, setSinglePayor] = useState(false);
  const [lastFetchedCriteria, setLastFetchedCriteria] = useState(null);

  const markets = useSelector((state) => MarketsUtil.getMarkets(state));
  const tagNamespaces = useSelector((state) =>
    StoreUtil.get(state.tagNamespaces, 'API_TAG_NAMESPACES_LIST_BY_CLIENT'),
  );
  const { getCriteria, updateCriteria, current } = useReportCriteria('payors');

  const { ready } = current;

  const [claimsType, setClaimsType] = useState(current.claimsType);

  const [marketOrTagFilter, setMarketOrTagFilter] = useState({});
  const clientId = useSelector((state) => state.app.selectedClient);

  
  const claims = useSelector((state) =>
    StoreUtil.get(state.analytics, (current.claimsType && current.claimsType==='organizations') ? prefix + 'CLAIMS_ORGANIZATION_BY_PAYOR' : prefix + 'CLAIMS_PROVIDER_BY_PAYOR'),
  );

  const [query, setQuery] = useState({
    offset: 0,
    limit: 25,
  });

  const onClaimsTypeChange = (event) => {
    setClaimsType(event.target.value);   
   updateCriteria({
      claimsType: event.target.value,
    });

  };

  const onPatientTypeChange = (event) => {
   updateCriteria({
      patientType: event.target.value,
    });

    setPrefix(event.target.value==='PEDIATRIC_PATIENTS' ? 'PEDIATRIC_' : '');

    //claims.data = [];
                     //claims.meta.loadStatus = 'STATUS_NONE'; //StoreUtil.setLoadStatus('STATUS_NONE', claims);    
  };

  useEffect(() => {
    dispatch(listMarkets(clientId));
    if (!tagNamespaces) {
      dispatch(listTagNamespacesByClient(clientId));
    }
  }, [clientId]);

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    if (!current.ready) {
      return;
    }
    debounceTimeout.current = setTimeout(() => {
      const criteria = getCriteria();
      if (
        !criteria ||
        !criteria.affiliated_client_id ||
        !(current.market || current.tag) ||
        !criteria.payors.length
      ) {
        return;
      }
      const filter = {
        ...(criteria.isMarket
          ? { market: { id: current.market } }
          : { tag: { id: current.tag } }),
        affiliated_client_id: criteria.affiliated_client_id,
        payors: criteria.payors,
        taxonomies: claimsType==='providers' ? criteria.taxonomies : null,
        affiliation: criteria.affiliation,
        organization_ids: criteria.organizations,
        singlePayor
      };

      // Track what criteria we're fetching for
      setLastFetchedCriteria(JSON.stringify({ current, query, singlePayor }));

      dispatch(
        loadAnalytics({
          type: current.claimsType==='providers' ? prefix + 'CLAIMS_PROVIDER_BY_PAYOR' : prefix + 'CLAIMS_ORGANIZATION_BY_PAYOR',
          filter,
          opts: query,
        }),
      );
    }, 100);
  }, [current, getCriteria, query, singlePayor]);
  useEffect(() => {
    if (current.ready) {
      // if we have markets loaded, payors loaded, but do not have a market or tag
      // then we need to default all of the state to something reasonable
      if (
        current &&
        !(current.market || current.tag) &&
        StoreUtil.isLoaded(markets) &&
        current.payors &&
        current.payors.length
      ) {
        updateCriteria({
          market: MarketsUtil.getDefaultMarket()?.id,
          isMarket: true,
        });
      }
    }
  }, [
    StoreUtil.isLoaded(markets),
    current?.payors,
    current?.ready
  ]);
  useEffect(() => {
    if (current?.isMarket !== undefined && current.ready) {
      if (current.isMarket) {
        if (!marketOrTagFilter?.market && markets?.data) {
          setMarketOrTagFilter({
            type: 'market',
            market: markets.data.find((m) => m.id === current.market),
          });
        }
      } else {
        if (!marketOrTagFilter?.tag && StoreUtil.isLoaded(tagNamespaces)) {
          setMarketOrTagFilter({
            type: 'tag',
            tag: TagsUtil.findTag(current.tag, tagNamespaces),
          });
        }
      }
    }
  }, [current, markets, marketOrTagFilter]);

  let data = claims?.data?.rows ?? [];
  const legendOrder = current?.payors ?? [];
  const payorMap = loadedPayors?.reduce((acc, payor, i) => {
    acc[payor.id] = { name: payor.name, color: colors[i] };
    return acc;
  }, {});
  if (!current.ready) {
    return <LoadingView />;
  }

  // Prepare chart data for PDF export
  const chartData = claims?.data?.rows?.map(row => {
    if (current.claimsType === 'providers') {
      return {
        provider: {
          id: row.provider_id,
          first_name: row.provider.first_name,
          last_name: row.provider.last_name,
          credential: row.provider.credential,
          npi: row.provider.npi,
          is_affiliated: row.provider.is_affiliated || row.provider.clients?.length > 0,
          clients: row.provider.clients,
        },
        organization_name: row.organization_name,
        total: row.total_claims || 0,
        affiliated: row.provider.is_affiliated || row.provider.clients?.length > 0,
        percent_0: row.percent_0,
        percent_1: row.percent_1,
        percent_2: row.percent_2,
        // Specialty fields for PDF
        specialty_doctivity_name: row.specialty_doctivity_name,
        specialty_classification: row.specialty_classification,
        specialty_specialization: row.specialty_specialization,
      };
    } else {
      return {
        organization: {
          id: row.organization_id,
          name: row.organization.name,
          npi: row.organization.npi,
          is_affiliated: row.organization.is_affiliated || row.organization.affiliated?.length > 0,
          affiliated: row.organization.affiliated,
          facility_type: row.organization.facility_type,
        },
        total: row.total_claims || 0,
        affiliated: row.organization.is_affiliated || row.organization.affiliated?.length > 0,
        percent_0: row.percent_0,
        percent_1: row.percent_1,
        percent_2: row.percent_2,
      };
    }
  }) || [];

  

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <FormControl
          variant='outlined'
          className={classes.headerDropDown}
          key='filter-type'
        >
          <InputLabel>Type</InputLabel>
          <Select
            value={current.claimsType}
            onChange={onClaimsTypeChange}
            label='Type'
          >
            <MenuItem value='providers' key='providers'>
              Providers
            </MenuItem>
            <MenuItem value='organizations' key='organizations'>
              Organizations
            </MenuItem>
          </Select>
        </FormControl>
        <ClaimsDateProvider />
      </div>
      <Card>
        <CardContent>
          <TitleTooltip
            title='Market Data'
            tooltip={
              `Compare up to 3 payors across a filtered list of ${current.claimsType}. Rollover each ${current.claimsType==='providers' ? 'provider' : 'organization'} to see the % per payor, including the remaining % with 'Other payor's.' Click into ${current.claimsType==='providers' ? 'a provider' : 'an organization'}  to see all payors on the payor pie chart.`
            }
            rightLabel={null && 'Based on claims from all ages'}
            onExport={current.claimsType==='providers' ? onExportClick : onExportOrgsClick}
            onPdfExport={() => setShowPdfExport(true)}
            csvExportOptions={{
              exportType: current.claimsType === 'providers'
                ? 'CLAIMS_PER_PROVIDER'
                : 'CLAIMS_PER_ORGANIZATION',
              totalCount: claims?.data?.count ?? claims?.data?.rows?.length,
              fileName: `doctivity_${current.claimsType === 'providers' ? 'PROVIDER' : 'ORGANIZATION'}_PERCENT_PAYOR_${DateUtil.formatDateForUser(new Date())}.csv`,
            }}
          />
          <div className={classes.filterContainer}>
            <div className={classes.dropDownContainer}>
              <FormControl
                variant='outlined'
                className={classes.filterDropDown}
                key='patient-type'
              >
                <InputLabel className={classes.filterLabel}>
                  Patients
                </InputLabel>
                <Select
                  key='select'
                  // default to ALL_PATIENTS until market data navigation is cleand up
                  value={current.patientType==='CAPTURED_CLAIMS' ? 'ALL_PATIENTS' : current.patientType}
                  onChange={onPatientTypeChange}
                  label='Patients'
                >
                  <MenuItem
                    value={PatientType.ALL_PATIENTS}
                    key='all_patients'
                  >
                    All Projected Patients
                  </MenuItem>
                  <MenuItem
                    value={PatientType.PEDIATRIC_PATIENTS}
                    key='pediatric_patients'
                  >
                    Captured Pediatric Patients
                  </MenuItem>
                </Select>
              </FormControl>
              <MarketOrTagSelect
                filter={marketOrTagFilter}
                onChange={(filter) => {
                  if (!ready) return;
                  setQuery({ ...query, offset: 0 });
                  updateCriteria({
                    market: filter?.market?.id,
                    tag: filter?.tag?.id,
                    isMarket: filter?.type === 'market',
                  });
                }}
                allowTagSelection={true}
                markets={markets}
                market={marketOrTagFilter?.market}
                type={current.claimsType}
                currentCriteria={current}
              />
              {current.claimsType==='providers' && (

                <TaxonomySelect
                  onChange={(taxonomies) => {
                    if (!ready) return;
                    const newTaxonomies = taxonomies && taxonomies.length > 0
                      ? taxonomies.map((ele) => ({ 'id': ele.code }))
                      : null;
                    setQuery({ ...query, offset: 0 });
                    updateCriteria({ taxonomies: newTaxonomies });
                  }}
                  values={current.taxonomies}
                />
  )}
              <FormControl
                variant='outlined'
                className={classes.filterDropDown}
                key='affiliated'
              >
                <InputLabel className={classes.filterLabel}>
                  Affiliation
                </InputLabel>
                <Select
                  key='select'
                  value={current.affiliation}
                  onChange={(event) => {
                    if (!ready) return;
                    setQuery({ ...query, offset: 0 });
                    updateCriteria({ affiliation: event.target.value });
                  }}
                  className={classes.select}
                  label='Affiliation'
                >
                  <MenuItem value='all' key='all'>
                    <em>All</em>
                  </MenuItem>
                  <MenuItem value='affiliated' key='affiliated'>
                    Affiliated
                  </MenuItem>
                  <MenuItem value='non-affiliated' key='non-affiliated'>
                    Non-Affiliated
                  </MenuItem>
                </Select>
              </FormControl>
              <OrganizationSelect
                value={current.organizations}
                onChange={(ids) => {
                  if (!ready) return;
                  setQuery({ ...query, offset: 0 });
                  // null (not []) so setQueryParams actually drops the param.
                  updateCriteria({
                    organizations: ids && ids.length > 0 ? ids : null,
                  });
                }}
              />
              <div className={classes.filterSecondRow}>
                <PayorSearch
                  initialNames={['MEDICARE', 'MEDICAID']}
                  limit={3}
                  ready={ready}
                  onPayorsChanged={(payors) => {
                    if (payors.length < 1) {
                      return;
                    }
                    setLoadedPayors(payors);
                    setQuery({ ...query, offset: 0 });
                    updateCriteria({ payors: payors.map((p) => p.id) });
                  }}
                  payorIds={current?.payors}
                />
              </div>
            </div>
          </div>
          {/* Check if data is stale (criteria changed since last fetch) */}
          {(() => {
            const currentCriteriaStr = JSON.stringify({ current, query, singlePayor });
            const isStaleData = lastFetchedCriteria !== currentCriteriaStr;
            const showLoading = StoreUtil.isLoading(claims) || isStaleData;
            
            return (
              <>
                {showLoading && <LoadingView />}
                {!showLoading && claims?.data?.rows?.length > 0 && (
                  <div className={classes.legend}>
                    {legendOrder.map((i) => {
                      const payor = payorMap[i];
                      if (!payor) return null;
                      return (
                        <div key={i} className={classes.legendRow}>
                          <span
                            style={{ backgroundColor: payor?.color }}
                            className={classes.legendItem}
                          />
                          <span className={classes.legendItemLabel}>
                            {payor.name}
                          </span>
                        </div>
                      );
                    })}
                    <div key='other' className={classes.legendRow}>
                      <span
                        style={{
                          backgroundColor: colors[colors.length - 1],
                          border: '1px solid #DFDBDB',
                        }}
                        className={classes.legendItem}
                      />
                      <span className={classes.legendItemLabel}>Other Payors</span>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          {claims &&
          StoreUtil.isLoaded(claims) &&
          lastFetchedCriteria === JSON.stringify({ current, query, singlePayor }) &&
          claims?.data?.rows?.length > 0 ? (
            <div className={classes.tableContainer}>
              {/* Header Row */}
              <div className={classes.headerRow}>
                {current.claimsType === 'providers' ? (
                  <>
                    <div className={`${classes.headerCell} ${classes.iconCol}`}></div>
                    <div className={`${classes.headerCell} ${classes.nameCol}`}>Provider</div>
                    <div className={`${classes.headerCell} ${classes.specialtyCol}`}>Specialty</div>
                    <div className={`${classes.headerCell} ${classes.orgCol}`}>Organization</div>
                    <div className={`${classes.headerCell} ${classes.chartCol}`}>Payor Mix</div>
                  </>
                ) : (
                  <>
                    <div className={`${classes.headerCell} ${classes.iconCol}`}></div>
                    <div className={`${classes.headerCell} ${classes.orgNameCol}`}>Organization</div>
                    <div className={`${classes.headerCell} ${classes.facilityTypeCol}`}>Facility Type</div>
                    <div className={`${classes.headerCell} ${classes.orgChartCol}`}>Payor Mix</div>
                  </>
                )}
              </div>

              {/* Data Rows */}
              {data.map((claim) => {
                let total =
                  (claim.percent_0 ?? 0) +
                  (claim.percent_1 ?? 0) +
                  (claim.percent_2 ?? 0);
                const otherPercent = Math.min(100, Math.max(0, 100 - total));
                
                const isAffiliated = current.claimsType === 'providers'
                  ? (claim.provider?.is_affiliated || claim.provider?.clients?.length > 0)
                  : (claim.organization?.is_affiliated || claim.organization?.affiliated?.length > 0);

                const title = (
                  <>
                    {current.claimsType === 'providers' && (
                      <span className={classes.tooltipHeader}>
                        {claim.provider.last_name}, {claim.provider.first_name}
                      </span>
                    )}
                    {current.claimsType === 'organizations' && (
                      <span className={classes.tooltipHeader}>
                        {claim.organization.name}
                      </span>
                    )}
                    <div className={classes.tooltipBody}>
                      {loadedPayors.map((payor, i) => (
                        <div key={payor.id} className={classes.tooltipLegendContainer}>
                          <div
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: 7,
                              marginRight: 8,
                              backgroundColor: payorMap[payor.id]?.color,
                            }}
                          />
                          {payorMap[payor.id]?.name}:&nbsp;
                          <span className={classes.tooltipPercent}>
                            {(claim[`percent_${i}`] ?? 0)?.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                      <div className={classes.tooltipLegendContainer}>
                        <div
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: 7,
                            marginRight: 8,
                            backgroundColor: colors[colors.length - 1],
                            border: '1px solid #DFDBDB',
                          }}
                        />
                        Other:&nbsp;
                        <span className={classes.tooltipPercent}>
                          {otherPercent.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </>
                );

                return (
                  <WhiteTooltip
                    key={`row-${current.claimsType === 'providers' ? claim.provider_id : claim.organization_id}`}
                    arrow
                    followCursor
                    title={title}
                  >
                    <Link
                      className={classes.dataRow}
                      to={current.claimsType === 'providers' 
                        ? `/providers/${claim.provider_id}/market`
                        : `/organizations/${claim.organization_id}/market`
                      }
                    >
                      {current.claimsType === 'providers' ? (
                        <>
                          {/* Icon Column */}
                          <div className={`${classes.cell} ${classes.iconCol}`}>
                            {isAffiliated && (
                              <AffiliatedIcon type='Provider' />
                            )}
                          </div>

                          {/* Provider Name */}
                          <div className={`${classes.cell} ${classes.nameCol}`}>
                            <span className={classes.providerName}>
                              {claim.provider.last_name}, {claim.provider.first_name}
                            </span>
                          </div>

                          {/* Specialty */}
                          <div className={`${classes.cell} ${classes.specialtyCol}`}>
                            <span className={classes.specialty}>
                              {SpecialtyUtil.getNameFromTaxonomy({
                                doctivity_name: claim.specialty_doctivity_name,
                                specialization: claim.specialty_specialization,
                                classification: claim.specialty_classification,
                                ...claim.provider?.taxonomy
                              }) || '-'}
                            </span>
                          </div>

                          {/* Organization */}
                          <div className={`${classes.cell} ${classes.orgCol}`}>
                            <span className={classes.organization}>
                              {claim.organization_name || '-'}
                            </span>
                          </div>

                          {/* Payor Mix Bar */}
                          <div className={`${classes.cell} ${classes.chartCol}`}>
                            <div className={classes.barContainer}>
                              {loadedPayors.map((payor, i) => {
                                const pct = claim[`percent_${i}`] ?? 0;
                                return (
                                  <div
                                    key={`${claim.provider_id}-bar-${i}`}
                                    className={classes.barSegment}
                                    style={{
                                      width: `${pct}%`,
                                      backgroundColor: payorMap[payor.id]?.color,
                                    }}
                                  >
                                    {pct >= 8 && (
                                      <span className={classes.barLabel}>
                                        {Math.round(pct)}%
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                              {otherPercent > 0 && (
                                <div
                                  className={classes.barSegment}
                                  style={{
                                    width: `${otherPercent}%`,
                                    backgroundColor: colors[colors.length - 1],
                                  }}
                                >
                                  {otherPercent >= 8 && (
                                    <span className={classes.barLabel} style={{ color: '#64748B', textShadow: 'none' }}>
                                      {Math.round(otherPercent)}%
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Icon Column */}
                          <div className={`${classes.cell} ${classes.iconCol}`}>
                            {isAffiliated && (
                              <AffiliatedIcon type='Organization' />
                            )}
                          </div>

                          {/* Organization Name */}
                          <div className={`${classes.cell} ${classes.orgNameCol}`}>
                            <span className={classes.orgName}>
                              {claim.organization.name}
                            </span>
                          </div>

                          {/* Facility Type */}
                          <div className={`${classes.cell} ${classes.facilityTypeCol}`}>
                            <span className={classes.facilityType}>
                              {claim.organization.facility_type_name || '-'}
                            </span>
                          </div>

                          {/* Payor Mix Bar */}
                          <div className={`${classes.cell} ${classes.orgChartCol}`}>
                            <div className={classes.barContainer}>
                              {loadedPayors.map((payor, i) => {
                                const pct = claim[`percent_${i}`] ?? 0;
                                return (
                                  <div
                                    key={`${claim.organization_id}-bar-${i}`}
                                    className={classes.barSegment}
                                    style={{
                                      width: `${pct}%`,
                                      backgroundColor: payorMap[payor.id]?.color,
                                    }}
                                  >
                                    {pct >= 8 && (
                                      <span className={classes.barLabel}>
                                        {Math.round(pct)}%
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                              {otherPercent > 0 && (
                                <div
                                  className={classes.barSegment}
                                  style={{
                                    width: `${otherPercent}%`,
                                    backgroundColor: colors[colors.length - 1],
                                  }}
                                >
                                  {otherPercent >= 8 && (
                                    <span className={classes.barLabel} style={{ color: '#64748B', textShadow: 'none' }}>
                                      {Math.round(otherPercent)}%
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </Link>
                  </WhiteTooltip>
                );
              })}
              <div className={classes.pagination}>
                <Paginateable
                  data={claims?.data}
                  query={query}
                  onQueryChange={(query) => setQuery(query)}
                />
              </div>
            </div>
          ) : (
            !StoreUtil.isLoading(claims) && 
            lastFetchedCriteria === JSON.stringify({ current, query, singlePayor }) && (
              <div className={classes.noData}>No data available</div>
            )
          )}
        </CardContent>
      </Card>
      <PDFExportDialog
        open={showPdfExport}
        onClose={() => setShowPdfExport(false)}
        title={`Payors - ${
          current.patientType === PatientType.PEDIATRIC_PATIENTS 
            ? 'Pediatric Patients' 
            : 'Projected Patients'
        } by ${current.claimsType === 'organizations' ? 'Organizations' : 'Providers'}`}
        reportType='payor_mix'
        chartData={chartData}
        totalCount={claims?.data?.count || chartData.length}
        payors={loadedPayors.map((p, i) => ({
          ...p,
          color: colors[i] || colors[colors.length - 1],
        }))}
        filters={{
          patientType: current.patientType,
          market: marketOrTagFilter?.market,
          tag: marketOrTagFilter?.tag,
          affiliation: current.affiliation,
          taxonomies: current.taxonomies,
          claimsDate: current.claimsDate,
          dateRange: `${new Date(
            latestClaimsDate.getFullYear() - 1,
            latestClaimsDate.getMonth() + 1,
            1
          ).toLocaleString('default', { 
            month: 'short', year: 'numeric' 
          })} - ${latestClaimsDate.toLocaleString('default', { 
            month: 'short', year: 'numeric' 
          })}`,
        }}
        defaultOptions={{
          claimsType: current.claimsType
        }}
      />
    </div>
  ); 
}

PayorChart.propTypes = {
  classes: PropTypes.object,
  getCriteria: PropTypes.func.isRequired,
};
const styled = withStyles(styles)(PayorChart);
export { styled as PayorChart };